import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { useDispatch } from "react-redux";
import { userLogoutAction } from "../../../redux/actions/loginImport";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetGolfLeagueData } from "../../../hooks/data/useGetGolfLeagueData";


const GolfLeagueContext = createContext();

export const useGolfLeagueContext = () => {
    const context = useContext(GolfLeagueContext);
    if (!context) {
        throw new Error("useGolfLeagueContext must be used within a GolfLeagueProvider");
    }
    return context;
}

export const GolfLeagueProvider = ({ children }) => {
    const { 
        masterPoolData,
        interval,
        setInterval,
    } = useUserAndGameContext();
    const redisKeys = masterPoolData?.redisKeys;

    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const [selectedPlayerId, setSelectedPlayerId] = useState(null);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [playerState, setPlayerState] = useState(null);
    //console.log("Player State", playerState);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const intervalParam = searchParams.get("interval");
    const teamIdParam = searchParams.get("teamId");
    const eventIdParam = searchParams.get("eventId");
    const playerIdParam = searchParams.get("playerId");

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [hasDataFetched, setHasDataFetched] = useState(false);

    const { mutate: getGolfLeagueData, data: golfLeagueData, isLoading: loadingGolfLeagueData, isError: errorGolfLeagueData, error: errorGolfLeagueDataMessage } = useGetGolfLeagueData();

    // Use Effect to fetch data when redisKeys are available
    useEffect(() => {
        if (redisKeys && !hasDataFetched) {
            getGolfLeagueData({ redisKeys });
            setHasDataFetched(true);
        }
    }, [redisKeys, hasDataFetched, getGolfLeagueData]);

    // Error handling for data fetching
    useEffect(() => {
        if (errorGolfLeagueData) {
            console.error("Error fetching golf league data:", errorGolfLeagueDataMessage);
            if (errorGolfLeagueDataMessage?.response?.status === 401) {
                dispatch(userLogoutAction());
                navigate("/login");
            } else {
                navigate("/404/pool-hall");
            }
        }
    }, [errorGolfLeagueData, errorGolfLeagueDataMessage, dispatch, navigate]);

    const { teams, players, events, currentInterval } = golfLeagueData || {};
    //console.log("Teams", teams, "Players", players, "Events", events, "Current Interval", currentInterval);

    // Use Effect on load, to set playerState to players once its loaded
    useEffect(() => {
        if (players) {
            setPlayerState(players);
        }
    }, [players]);

    const firstInterval = useMemo(() => masterPoolData?.gameParameters?.firstInterval, [masterPoolData]);
    const lastInterval = useMemo(() => masterPoolData?.gameParameters?.lastInterval, [masterPoolData]);

    useEffect(() => {
        if (currentInterval !== undefined && firstInterval !== undefined && lastInterval !== undefined) {
            let intervalToUse;
            
            // Check if URL interval parameter exists and is valid
            if (intervalParam) {
                const parsedInterval = parseInt(intervalParam, 10);
                
                // Validate that the interval is within the allowed range
                if (!isNaN(parsedInterval) && parsedInterval >= firstInterval && parsedInterval <= lastInterval) {
                    intervalToUse = parsedInterval;
                } else {
                    // If invalid, update URL to use current interval
                    const newURL = new URL(window.location.href);
                    newURL.searchParams.set('interval', currentInterval);
                    window.history.replaceState({}, '', newURL);
                    intervalToUse = currentInterval;
                }
            } else {
                // No interval parameter, use current interval
                intervalToUse = currentInterval;
            }
            //console.log("Interval to use:", intervalToUse);
            // Set the interval in context
            setInterval(intervalToUse);
        }
    }, [intervalParam, currentInterval, firstInterval, lastInterval, setInterval]);

    //Use Effect to set selected IDs from URL parameters
    useEffect(() => {
        if (teamIdParam) {
            setSelectedTeamId(teamIdParam);
        }
        if (eventIdParam) {
            setSelectedEventId(eventIdParam);
        }
        if (playerIdParam) {
            setSelectedPlayerId(playerIdParam);
        }
    }, [teamIdParam, eventIdParam, playerIdParam]);

   // Intervals to show, in reverse order, goes from currentInterval to firstInterval
    const intervalsToShow = useMemo(() => {
        const intervals = [];
        for (let i = firstInterval; i <= currentInterval; i++) {
        intervals.push(i);
        }
        return intervals.reverse();
  }, [firstInterval, currentInterval]);

    const GolfLeagueContextValues = useMemo(() => {
        const findTournamentId = (events, intervalNumber) => {
            const tournament = events?.find((event) => Number(event.interval) === Number(intervalNumber));
            return tournament ? tournament.tournamentId : null;
        };
      
        const findTournamentInfo = (team, tournamentId) => {
            //console.log("Finding tournament info for team:", team, "tournamentId:", tournamentId);
            // Return null if any required data is missing
            if (!team || !tournamentId || !team.scores) {
                return null;
            }
            
            // Convert tournamentId to string if it's a number
            const tournamentIdStr = tournamentId.toString();
            
            // Get the tournament score info from the team's scores
            const tournamentInfo = team.scores[tournamentIdStr];
            //console.log("!!Tournament Info!!", tournamentInfo);
            
            if (!tournamentInfo) {
                return null;
            }
            
            // Return the rank, score, and tied status
            return {
                rank: tournamentInfo.rank || tournamentInfo.status || '-',
                score: tournamentInfo.score !== undefined && tournamentInfo.score !== null ? tournamentInfo.score : null,
                round1: tournamentInfo.round1,
                round2: tournamentInfo.round2,
                status: tournamentInfo.status,
                tied: tournamentInfo.tied || false // Default to false if tied property doesn't exist
            };
        };

        return {
            players,
            events,
            teams,
            currentInterval,
            firstInterval,
            lastInterval,
            selectedTeamId,
            selectedPlayerId,
            selectedEventId,
            intervalsToShow,
            findTournamentId,
            findTournamentInfo,
            playerState,
            setPlayerState,
        };
    }, [players, events, teams, currentInterval, firstInterval, lastInterval, selectedTeamId, selectedPlayerId, selectedEventId, intervalsToShow, playerState, setPlayerState]);

    // Add LoadingScreen state
    if (!hasDataFetched || loadingGolfLeagueData || interval === 0) {
        return (
            <LoadingScreen />
        );
    };

    return (
        <GolfLeagueContext.Provider value={GolfLeagueContextValues}>
            {children}
        </GolfLeagueContext.Provider>
    );
}

export default GolfLeagueProvider;